/**
 * Fetch what a realistic biome needs (biomeAssets.ts) and warm the scene's
 * caches, ahead of time while the page has idle time (the pairing, start,
 * and ambient screens; App.tsx) or on demand when the phone names a biome
 * the page has not seen (PuttStage.tsx). Everything is fetched once per
 * page: the tree sets land in bakedTreeData.ts, the atlas bytes in
 * stageAssets.ts, where the scene's own requests find them.
 */
import type { RealisticSceneryId } from '../../../packages/putt-scene/src/realistic/index';
import { biomeAtlasKeys, biomeTreeSet } from './biomeAssets';
import { bakedTreesLoaded, loadBakedTrees } from './bakedTreeData';
import { stageAssets } from './stageAssets';
import type { StageGraphics } from './quality';

/** True when the biome's vegetation can be meshed right now. The atlas is
 *  not part of this: the scene fetches it itself and fades it in. */
export function biomeMeshable(sceneryId: RealisticSceneryId): boolean {
  const set = biomeTreeSet(sceneryId);
  return set == null || bakedTreesLoaded(set);
}

/**
 * Load the tree set and the atlas. `onProgress` gets 0..1 as the pieces
 * land. Rejects only when the tree set cannot be fetched (the scene cannot
 * mesh without it); a failed atlas fetch is the scene's own warning.
 */
export async function preloadBiome(
  sceneryId: RealisticSceneryId,
  graphics: StageGraphics,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  const set = biomeTreeSet(sceneryId);
  const atlases = biomeAtlasKeys(sceneryId, graphics);
  const steps = (set ? 1 : 0) + atlases.length;
  let done = 0;
  const step = () => {
    done += 1;
    onProgress?.(steps ? done / steps : 1);
  };
  const atlasJobs = atlases.map((key) => stageAssets.loadRawRgba(key).then(step, () => undefined));
  if (set) {
    await loadBakedTrees(set);
    step();
  }
  await Promise.all(atlasJobs);
  onProgress?.(1);
}
